<template>
  <div class="screen">
    <div class="left">
      <checkbox v-if="!viewonly" :value="value" :label="label" @input="changed"/>
      <span class="label" v-else>{{ label }}</span>
      <router-link v-if="editLink" :to="editLink" class="edit-link">{{ $t('buttons.edit') }}</router-link>
      <transition name="enable">
        <div class="files" v-if="expanded">
          <div class="upload-field" v-for="(file, index) in files" :key="file.id">
            <div class="upload-size">{{ file.width }} x {{ file.height }} px</div>
            <dropzone
              :value="file.newKey || file.image"
              :image-width="file.width"
              :image-height="file.height"
              :screen-width="file.screenWidth"
              :screen-height="file.screenHeight"
              :screen-offset-top="file.offsetTop"
              :screen-offset-left="file.offsetLeft"
              :preview-src="file.previewSrc"
              :viewonly="viewonly"
              :background="file.backgroundColor"
              @input="imageUpdated($event, index)"
              @upload-key="fileUpdated($event, index)"
            />
          </div>
          <slot></slot>
        </div>
      </transition>
    </div>
    <div class="outer-right">
      <div class="expand-arrow" @click="expanded = !expanded">
        <ExpandArrow :class="{ active: expanded }" />
      </div>
      <transition name="enable">
        <div class="right" v-if="expanded">
          <p>{{ $t('create.uploadOnScreenPreview') }}:</p>
          <preview-screen
            :image="mainImage"
            :image-width="mainFile.width"
            :image-height="mainFile.height"
            :image-offset-top="mainFile.offsetTop"
            :image-offset-left="mainFile.offsetLeft"
            :overlay="mainPreviewSrc"
            :overlay-width="mainFile.screenWidth"
            :overlay-height="mainFile.screenHeight"
            :background="mainFile.backgroundColor"
            @click.native="openModal"
          />
        </div>
      </transition>
    </div>
    <modal class="modal-screens" v-show="isModalOpen" @close="closeModal">
      <template v-slot:body>
        <h2>{{ label }}</h2>
        <div class="modal-previews">
          <div v-for="file in files" :key="file.id" class="modal-screen">
            <h3>{{ file.screen === '4x3' ? $t('create.uploadStandard') : $t('create.uploadWidescreen') }} ({{ file.width }} x {{ file.height }} px)</h3>
            <preview-screen
              :image="file.image"
              :image-width="file.width"
              :image-height="file.height"
              :image-offset-top="file.offsetTop"
              :image-offset-left="file.offsetLeft"
              :overlay="file.previewSrc"
              :overlay-width="file.screenWidth"
              :overlay-height="file.screenHeight"
              :background="file.backgroundColor"
            />
          </div>
        </div>
      </template>
    </modal>
  </div>
</template>

<script>
import Checkbox from './Checkbox.vue'
import Dropzone from './Dropzone.vue'
import PreviewScreen from './PreviewScreen.vue'
import Modal from './Modal.vue'
import ExpandArrow from '@/assets/images/icons/arrow_expand.svg'
import { SET_NEW_UPLOAD_KEY, SET_NEW_UPLOAD_IMAGE } from '@/store/mutations.type'

export default {
  name: 'upload-screen',
  components: {
    Checkbox,
    Dropzone,
    PreviewScreen,
    Modal,
    ExpandArrow,
  },
  props: {
    value: Boolean,
    label: String,
    files: Array,
    viewonly: {
      type: Boolean,
      default: false
    },
    editLink: {
      type: String,
      default: null
    },
    language: String,
  },
  data() {
    return {
      isModalOpen: false,
      expanded: this.value,
    }
  },
  computed: {
    mainPreviewSrc() {
      return this.files.length ? this.files[0].previewSrc : null
    },
    mainFile() {
      return this.files.length ? this.files[0] : null
    },
    mainImage() {
      return this.mainFile.image ? this.mainFile.image : null
    },
    previewStyles() {
      const styles = {}
      if (this.files.length) {
        styles['padding-bottom'] =
          (100 * this.files[0].screenHeight) / this.files[0].screenWidth + '%'
      }
      return styles
    },
    previewImageStyles() {
      const styles = {}
      if (this.mainFile && this.mainFile.key) {
        styles['top'] =
          (100 * this.mainFile.offsetTop) / this.mainFile.screenHeight + '%'
        styles['left'] =
          (100 * this.mainFile.offsetLeft) / this.mainFile.screenWidth + '%'
        styles['width'] =
          (100 * this.mainFile.width) / this.mainFile.screenWidth + '%'
      }
      return styles
    }
  },
  methods: {
    changed(newValue) {
      this.$emit('input', newValue)
      this.expanded = newValue;
    },
    openModal() {
      this.isModalOpen = true
    },
    closeModal() {
      this.isModalOpen = false
    },
    imageUpdated(image, index) {
      this.$store.commit(SET_NEW_UPLOAD_IMAGE, {
        id: this.files[index].id,
        image,
        lang: this.language,
      })
      this.$emit('updated', image)
    },
    fileUpdated(key, index) {
      this.$store.commit(SET_NEW_UPLOAD_KEY, {
        id: this.files[index].id,
        key,
        lang: this.language,
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.enable-enter,
.enable-leave-to {
  opacity: 0;
  max-height: 0;
  overflow: hidden;
}
.enable-enter-to,
.enable-leave {
  max-height: 500px;
}
.enable-enter-active,
.enable-leave-active {
  transition: opacity 500ms ease, max-height 500ms ease;
}
.screen {
  background-color: #fff;
  display: flex;

  .label {
    font-weight: 600;
  }

  .edit-link {
    margin-left: 25px;
  }

  .left {
    text-align: left;
    flex: 1;
  }
  .outer-right {
    .expand-arrow {
      text-align: right;

      svg {
        height: 10px;
        width: 20px;
        cursor: pointer;
      }

      svg.active {
        transform: rotate(180deg);
      }
    }

    .right {
      color: #898989;
      text-align: left;
      border-left: 1px solid #ddd;
      flex: 0 0 270px;
      margin: 25px 0 -14px 50px;
      padding: 0 0 0 50px;
      width: 270px;

      p {
        margin: 0 0 em(15);
      }

      .preview {
        cursor: pointer;
      }
    }
  }
  .upload-field {
    margin-top: 25px;
  }
  .upload-size {
    color: #898989;
  }
}
.modal-screens {
  h2 {
    margin-bottom: 25px;
  }
  /deep/ .modal-previews {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    flex-wrap: wrap;

    h3 {
      color: #898989;
      font-weight: 300;
      margin-bottom: 10px;
    }

    .modal-screen {
      max-width: 26vw;

      & + .modal-screen {
        margin-left: 25px;
        max-width: 34.8vw;
      }
    }
  }
}
</style>
